(() => {
  const t = (key, fallback, variables = {}) => window.nawahTranslate?.(key, fallback, variables) || fallback || key;
  'use strict';

  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_ADD_URL = `${ROOT}cart/add.js`;
  const CART_URL = `${ROOT}cart.js`;
  const OFFER_VERSION = '35.0.0';

  function readGiftConfig() {
    try {
      return JSON.parse(document.getElementById('NawahGiftConfig')?.textContent || '{}');
    } catch (error) {
      console.warn('NAWAH: Body Splash gift configuration could not be read.', error);
      return {};
    }
  }

  function uniqueId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `nawah-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  async function readJson(response, fallback) {
    const text = await response.text();
    try {
      const data = JSON.parse(text);
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || fallback || `Request failed (${response.status}).`);
      }
      return data;
    } catch (error) {
      if (error instanceof SyntaxError) throw new Error(fallback || `Unexpected response (${response.status}).`);
      throw error;
    }
  }

  class NawahProduct {
    constructor(root) {
      this.root = root;
      this.variants = this.readVariants();
      this.form = root.querySelector('[data-nawah-product-form]');
      this.variantInput = root.querySelector('[data-nawah-variant-id]');
      this.price = root.querySelector('[data-nawah-price]');
      this.comparePrice = root.querySelector('[data-nawah-compare-price]');
      this.addButton = root.querySelector('[data-nawah-add]');
      this.addLabel = root.querySelector('[data-nawah-add-label]');
      this.addPrice = root.querySelector('[data-nawah-add-price]');
      this.status = root.querySelector('[data-nawah-status]');
      this.sticky = root.querySelector('[data-nawah-sticky]');
      this.stickyButton = root.querySelector('[data-nawah-sticky-submit]');
      this.stickyLabel = root.querySelector('[data-nawah-sticky-label]');
      this.stickyPrice = root.querySelector('[data-nawah-sticky-price]');
      this.stickyQuantity = root.querySelector('[data-nawah-sticky-qty]');
      this.stickyQuantityMinus = root.querySelector('[data-nawah-sticky-qty-minus]');
      this.stickyQuantityPlus = root.querySelector('[data-nawah-sticky-qty-plus]');
      this.quantityInput = root.querySelector('input[name="quantity"]');
      this.moneyFormat = window.Shopify?.currency?.active || '';
      this.variantAvailable = Boolean(this.addButton && !this.addButton.dataset.soldOutInitial && !this.addButton.disabled);

      this.offer = root.querySelector('[data-body-splash-offer]');
      this.offerSlots = this.offer?.querySelector('[data-body-splash-slots]') || null;
      this.offerTemplate = this.offer?.querySelector('[data-body-splash-slot-template]') || null;
      this.offerStatus = this.offer?.querySelector('[data-body-splash-status]') || null;
      this.offerReady = this.offer?.dataset.bodySplashReady === 'true';
      this.bodySplashSelections = [];
      this.giftConfig = readGiftConfig();
      this.allowedGiftVariantIds = new Set(String(this.offer?.dataset.allowedGiftVariantIds || '').split(',').map((value) => Number(value)).filter(Number.isFinite));
      this.bodySplashVariants = (this.giftConfig?.bodySplash?.variants || [])
        .filter((variant) => Number.isFinite(Number(variant.variantId)) && variant.available !== false)
        .filter((variant) => this.allowedGiftVariantIds.size === 0 || this.allowedGiftVariantIds.has(Number(variant.variantId)));

      const selectedVariant = this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value));
      if (selectedVariant) this.variantAvailable = Boolean(selectedVariant.available);

      this.bindGallery();
      this.bindOptions();
      this.updateBundlePreviews();
      this.bindQuantity();
      this.bindSticky();
      this.bindZoom();
      this.bindBodySplashOffer();
      this.syncAddState();
    }

    readVariants() {
      const node = this.root.querySelector('[data-nawah-variants]');
      if (!node) return [];
      try { return JSON.parse(node.textContent); }
      catch (error) {
        console.error('NAWAH: invalid variants JSON', error);
        return [];
      }
    }

    mediaButtons() {
      return [...this.root.querySelectorAll('[data-media-target]')];
    }

    activeMediaIndex() {
      return this.mediaButtons().findIndex((button) => button.classList.contains('is-active'));
    }

    bindGallery() {
      this.mediaButtons().forEach((button, index) => {
        button.addEventListener('click', () => this.showMedia(String(button.dataset.mediaTarget), index + 1));
      });

      const stage = this.root.querySelector('[data-gallery-stage]');
      if (!stage) return;
      let startX = 0;
      stage.addEventListener('touchstart', (event) => { startX = event.changedTouches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) < 45) return;
        const buttons = this.mediaButtons();
        if (buttons.length < 2) return;
        const current = this.activeMediaIndex();
        const next = delta < 0 ? Math.min(current + 1, buttons.length - 1) : Math.max(current - 1, 0);
        buttons[next]?.click();
      }, { passive: true });
    }

    bindZoom() {
      this.root.querySelectorAll('[data-zoom-surface]').forEach((surface) => {
        surface.addEventListener('mousemove', (event) => {
          const rect = surface.getBoundingClientRect();
          surface.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
          surface.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
        });
      });
    }

    showMedia(mediaId, index) {
      this.root.querySelectorAll('[data-media-id]').forEach((media) => {
        media.classList.toggle('is-active', String(media.dataset.mediaId) === String(mediaId));
      });
      this.mediaButtons().forEach((button) => {
        button.classList.toggle('is-active', String(button.dataset.mediaTarget) === String(mediaId));
      });
      const counter = this.root.querySelector('[data-current-media]');
      if (counter && index) counter.textContent = index;
    }

    bindOptions() {
      this.root.querySelectorAll('[data-option-position]').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });
    }

    selectedOptions() {
      return [...this.root.querySelectorAll('[data-option-position]:checked')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      this.root.querySelectorAll('[data-selected-option]').forEach((label) => {
        const position = Number(label.dataset.selectedOption);
        label.textContent = selected[position - 1] || '';
      });
      this.updateBundlePreviews();
      if (!variant) {
        this.setUnavailable(t('unavailable', 'Unavailable'));
        return;
      }
      this.updateVariant(variant);
    }

    updateBundlePreviews() {
      const checkedInputs = [...this.root.querySelectorAll('[data-bundle-preview-target]:checked')];
      checkedInputs.forEach((input) => {
        const target = input.dataset.bundlePreviewTarget;
        const preview = [...this.root.querySelectorAll('[data-bundle-preview]')]
          .find((item) => item.dataset.bundlePreview === target);
        if (!preview) return;
        const image = preview.querySelector('[data-bundle-preview-image]');
        const title = preview.querySelector('[data-bundle-preview-title]');
        if (title) title.textContent = input.dataset.bundlePreviewTitle || input.value;
        if (image && input.dataset.bundlePreviewImage) {
          image.src = input.dataset.bundlePreviewImage;
          image.removeAttribute('srcset');
          image.alt = input.dataset.bundlePreviewTitle || input.value;
        }
      });
    }

    updateVariant(variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
      this.updateUrl(variant.id);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      if (variant.featured_media?.id) {
        const button = this.root.querySelector(`[data-media-target="${variant.featured_media.id}"]`);
        const index = button ? this.mediaButtons().indexOf(button) + 1 : null;
        this.showMedia(String(variant.featured_media.id), index);
      }
    }

    updateUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.moneyFormat || 'EGP',
      }).format(cents / 100);
    }

    updatePrice(variant) {
      const price = this.formatMoney(variant.price);
      if (this.price) this.price.textContent = price;
      if (this.addPrice) this.addPrice.textContent = price;
      if (this.stickyPrice) this.stickyPrice.textContent = price;
      if (!this.comparePrice) return;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePrice.hidden = false;
      } else {
        this.comparePrice.hidden = true;
      }
    }

    updateAvailability(variant) {
      this.variantAvailable = Boolean(variant.available);
      this.syncAddState();
      if (this.status) this.status.textContent = '';
    }

    setUnavailable(message) {
      this.variantAvailable = false;
      if (this.addButton) this.addButton.disabled = true;
      if (this.addLabel) this.addLabel.textContent = message;
      if (this.stickyButton) this.stickyButton.disabled = true;
      if (this.stickyLabel) this.stickyLabel.textContent = message;
      if (this.status) this.status.textContent = message;
    }

    quantity() {
      return Math.max(1, Number(this.quantityInput?.value || 1));
    }

    setQuantity(value) {
      const next = Math.max(Number(this.quantityInput?.min || 1), Number(value || 1));
      if (this.quantityInput) this.quantityInput.value = String(next);
      if (this.stickyQuantity) this.stickyQuantity.value = String(next);
      this.syncBodySplashSlots();
    }

    bindQuantity() {
      const minus = this.root.querySelector('[data-qty-minus]');
      const plus = this.root.querySelector('[data-qty-plus]');
      if (!this.quantityInput || !minus || !plus) return;
      minus.addEventListener('click', () => this.setQuantity(this.quantity() - 1));
      plus.addEventListener('click', () => this.setQuantity(this.quantity() + 1));
      this.quantityInput.addEventListener('change', () => this.setQuantity(this.quantityInput.value));
      this.stickyQuantityMinus?.addEventListener('click', () => this.setQuantity(Number(this.stickyQuantity?.value || this.quantity()) - 1));
      this.stickyQuantityPlus?.addEventListener('click', () => this.setQuantity(Number(this.stickyQuantity?.value || this.quantity()) + 1));
      this.stickyQuantity?.addEventListener('change', () => this.setQuantity(this.stickyQuantity.value));
      this.setQuantity(this.quantity());
    }

    bindSticky() {
      if (!this.sticky || !this.addButton) return;
      const observer = new IntersectionObserver(([entry]) => {
        this.sticky.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0.1 });
      observer.observe(this.addButton);
      this.stickyButton?.addEventListener('click', () => {
        if (this.quantityInput && this.stickyQuantity) this.setQuantity(this.stickyQuantity.value);
        this.addButton.click();
      });
    }

    bindBodySplashOffer() {
      if (!this.offer) return;
      this.syncBodySplashSlots();
      this.form?.addEventListener('submit', (event) => this.handleBodySplashSubmit(event));
    }

    syncBodySplashSlots() {
      if (!this.offer || !this.offerReady || !this.offerSlots || !this.offerTemplate) {
        this.syncAddState();
        return;
      }
      const quantity = this.quantity();
      this.bodySplashSelections = this.bodySplashSelections.slice(0, quantity);
      while (this.bodySplashSelections.length < quantity) this.bodySplashSelections.push(null);
      this.offerSlots.innerHTML = '';

      for (let index = 0; index < quantity; index += 1) {
        const fragment = this.offerTemplate.content.cloneNode(true);
        const slot = fragment.querySelector('[data-body-splash-slot]');
        const label = fragment.querySelector('[data-body-splash-slot-label]');
        if (label) label.textContent = quantity === 1 ? t('your_gift_scent', 'Your gift scent') : t('gift_number_of_total', 'Gift {{ number }} of {{ total }}', { number: index + 1, total: quantity });
        fragment.querySelectorAll('[data-body-splash-choice]').forEach((button) => {
          const variantId = Number(button.dataset.variantId);
          const scent = button.dataset.scent || '';
          const selected = Number(this.bodySplashSelections[index]?.variantId) === variantId;
          button.classList.toggle('is-selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          button.addEventListener('click', () => {
            const validVariant = this.bodySplashVariants.find((variant) => Number(variant.variantId) === variantId);
            if (!validVariant) return;
            this.bodySplashSelections[index] = { variantId, scent };
            slot?.querySelectorAll('[data-body-splash-choice]').forEach((choice) => {
              const isSelected = choice === button;
              choice.classList.toggle('is-selected', isSelected);
              choice.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            });
            this.syncAddState();
          });
        });
        this.offerSlots.appendChild(fragment);
      }
      this.syncAddState();
    }

    offerComplete() {
      if (!this.offer) return true;
      if (!this.offerReady || this.bodySplashVariants.length < 1) return false;
      const quantity = this.quantity();
      if (this.bodySplashSelections.length !== quantity) return false;
      return this.bodySplashSelections.every((selection) => {
        if (!selection) return false;
        return this.bodySplashVariants.some((variant) => Number(variant.variantId) === Number(selection.variantId));
      });
    }

    syncAddState() {
      if (!this.addButton) return;
      const availableLabel = this.addButton.dataset.availableLabel || t('add_to_bag', 'Add to bag');
      const soldOutLabel = this.addButton.dataset.soldOutLabel || t('sold_out', 'Sold out');
      let disabled = !this.variantAvailable;
      let label = this.variantAvailable ? availableLabel : soldOutLabel;

      if (this.offer) {
        if (!this.offerReady || this.bodySplashVariants.length < 1) {
          disabled = true;
          label = t('gift_unavailable', 'Gift unavailable');
          if (this.offerStatus) this.offerStatus.textContent = t('five_scents_unavailable', 'The five complimentary scents are not ready yet.');
        } else {
          const missing = this.bodySplashSelections.filter((selection) => !selection).length;
          if (missing > 0) {
            disabled = true;
            label = missing === 1 ? t('choose_gift_scent', 'Choose gift scent') : t('choose_gift_scents', 'Choose {{ count }} gift scents', { count: missing });
            if (this.offerStatus) this.offerStatus.textContent = missing === 1 ? t('choose_one_scent_to_continue', 'Choose 1 scent to continue.') : t('choose_scents_to_continue', 'Choose {{ count }} scents to continue.', { count: missing });
          } else if (this.offerStatus) {
            this.offerStatus.textContent = this.quantity() === 1
              ? t('selected_scent', 'Selected: {{ scent }}', { scent: this.bodySplashSelections[0].scent })
              : t('all_scents_selected', 'All {{ count }} complimentary scents selected.', { count: this.quantity() });
          }
        }
      }

      this.addButton.disabled = disabled;
      if (this.addLabel) this.addLabel.textContent = label;
      if (this.stickyButton) this.stickyButton.disabled = disabled;
      if (this.stickyLabel) this.stickyLabel.textContent = label;
    }

    selectedGiftItems(instanceId) {
      const aggregated = new Map();
      for (const selection of this.bodySplashSelections) {
        const key = Number(selection.variantId);
        const current = aggregated.get(key) || { quantity: 0, scent: selection.scent };
        current.quantity += 1;
        aggregated.set(key, current);
      }
      return [...aggregated.entries()].map(([variantId, entry]) => ({
        id: variantId,
        quantity: entry.quantity,
        properties: {
          _nawah_auto_gift: 'true',
          _nawah_gift_kind: 'body-splash',
          _nawah_gift_scent: entry.scent,
          _nawah_gift_name: `${this.giftConfig?.bodySplash?.label || 'UNO Body Splash'} — ${entry.scent}`,
          _nawah_gift_engine: OFFER_VERSION,
          _nawah_offer_instance: instanceId,
        },
      }));
    }

    sectionRequest(drawer) {
      const ids = drawer?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || ['cart-drawer', 'cart-icon-bubble'];
      return {
        sections: [...new Set(ids)],
        sections_url: `${window.location.pathname}${window.location.search}`,
      };
    }

    async fetchCart() {
      const response = await fetch(CART_URL, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      return readJson(response, t('unable_to_refresh_cart', 'Unable to refresh the cart.'));
    }

    async handleBodySplashSubmit(event) {
      if (!this.offer) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.offerComplete()) {
        this.syncAddState();
        this.offer.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!this.variantAvailable || !this.variantInput?.value || this.addButton.disabled) return;

      const instanceId = uniqueId();
      const selections = this.bodySplashSelections.map((selection) => Number(selection.variantId));
      const labels = this.bodySplashSelections.map((selection) => selection.scent);
      const parent = {
        id: Number(this.variantInput.value),
        quantity: this.quantity(),
        properties: {
          _nawah_offer_instance: instanceId,
          _nawah_offer_version: OFFER_VERSION,
          _nawah_offer_name: t('dry_oil_body_splash_title', 'Dry Oil + UNO Body Splash'),
          _nawah_body_splash_choices: JSON.stringify(selections),
          _nawah_body_splash_labels: JSON.stringify(labels),
        },
      };
      const drawer = document.querySelector('cart-drawer');
      const payload = {
        items: [parent, ...this.selectedGiftItems(instanceId)],
        ...this.sectionRequest(drawer),
      };

      this.addButton.disabled = true;
      this.addButton.classList.add('is-loading');
      this.addButton.setAttribute('aria-busy', 'true');
      if (this.stickyButton) this.stickyButton.disabled = true;
      if (this.status) this.status.textContent = t('adding_dry_oil_gift', 'Adding your Dry Oil and complimentary Body Splash…');
      document.dispatchEvent(new CustomEvent('nawah:cart-mutation-start', { detail: { source: 'body-splash-offer' } }));

      let cart = null;
      try {
        const response = await fetch(CART_ADD_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
        const data = await readJson(response, t('offer_add_error', 'Shopify could not add this offer. Please try again.'));
        cart = await this.fetchCart();

        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
          drawer.setActiveElement?.(this.addButton);
          drawer.renderContents?.(data);
        }

        document.dispatchEvent(new CustomEvent('nawah:cart-mutation-end', {
          detail: { source: 'body-splash-offer', success: true, cart },
        }));

        if (window.NawahGiftEngine?.sync) {
          const syncedCart = await window.NawahGiftEngine.sync();
          if (syncedCart?.items) cart = syncedCart;
        }

        if (!drawer) {
          window.location.assign(window.routes?.cart_url || `${ROOT}cart`);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'nawah-body-splash-offer',
              productVariantId: this.variantInput.value,
              cartData: cart,
            });
          } catch (error) {
            console.warn('NAWAH: cart subscribers did not complete.', error);
          }
        }
        document.dispatchEvent(new CustomEvent('nawah:cart-updated', { detail: { cart } }));
        if (this.status) this.status.textContent = t('added_dry_oil_gift', 'Added with your complimentary Body Splash.');
      } catch (error) {
        console.error('NAWAH Body Splash offer add failed:', error);
        document.dispatchEvent(new CustomEvent('nawah:cart-mutation-end', {
          detail: { source: 'body-splash-offer', success: false },
        }));
        if (this.status) this.status.textContent = error.message || t('unable_to_add_offer', 'Unable to add this offer. Please try again.');
      } finally {
        this.addButton.classList.remove('is-loading');
        this.addButton.removeAttribute('aria-busy');
        this.syncAddState();
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-nawah-product]').forEach((root) => {
      if (root.dataset.nawahInitialized === 'true') return;
      root.dataset.nawahInitialized = 'true';
      new NawahProduct(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
